import { useMemo, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import type { WebTask } from '../../../lib/tasks';
import { AgentDot } from '../../AgentDot';
import { Modal } from '../../Modal';
import { Badge } from '../ui';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { OutreachKindBadge } from './OutreachKindBadge';
import { OutreachNoReplyNeeded } from './OutreachNoReplyNeeded';
import { OutreachReplyComposer } from './OutreachReplyComposer';
import styles from './outreach-thread.module.css';

/**
 * The correspondence behind an inbound outreach row — who said what, when, and where it was waiting
 * on the user — plus the one thing anyone opening a message wants: a reply box.
 *
 * It renders the messages IN PLACE rather than opening the conversation they were delivered into, and
 * that is not a shortcut: an inbound outreach lands in a SCHEDULER conversation created for it on the
 * receiving side, which is not in the user's chat list, so navigating to `?conversation=<id>` finds
 * nothing to select and lands on the root — which is exactly what a click used to do.
 *
 * Each message shows a RELATIVE time, because that is what you scan a thread with, and carries the
 * exact instant one interaction away: `title` for a pointer, tap-to-reveal for touch (a `title`
 * tooltip does not exist on a phone).
 *
 * Two honest limits are visible in the markup rather than papered over:
 *  - a message written before per-message dates existed has NO date, and renders without one instead
 *    of borrowing the thread's;
 *  - `createdDate` is wall-clock in the outreach SITE's timezone, so across a timezone gap the
 *    relative figure drifts by that offset. That is why the absolute value is always reachable.
 *
 * It renders its OWN <Modal>, because the drill-in host (`TaskSidePanel`) is a bare `{modal}` slot —
 * every detail view owns its scrim, close, Escape stack and portal. Written after the side-panel →
 * modal cutover (#7556) converted the other eight, this one shipped as a naked <div> and therefore
 * painted itself unstyled at the top of the page instead of opening. The header stays in the BODY
 * rather than the Modal's `title` so the AgentDot can sit beside it; `labelledBy` points the dialog's
 * accessible name at that heading. `fullScreenOnSmall` is the Modal default, so narrow viewports get
 * the full-screen sheet with a leading back chevron and wide gets the centered card.
 */
export function OutreachThreadModal({ task }: { task: WebTask }) {
  const { t } = useTranslation();
  const { closeModal } = useTaskSidePanel();
  const entries = task.outreachMessages ?? [];
  const title = task.senderName ? t('tasks.outreach.title', { name: task.senderName }) : t('tasks.untitledTask');
  // One `now` for the whole render, so two messages a second apart cannot disagree about "just now".
  const now = useMemo(() => new Date(), []);
  const [revealed, setRevealed] = useState<number | null>(null);

  const agent = useAgentName();

  return (
    <Modal
      open
      onClose={closeModal}
      size="medium"
      maxHeightPx={650}
      scrollBody
      labelledBy="outreach-thread-title"
    >
      <div className={styles.body}>
        <header className={styles.header}>
          <AgentDot id={task.id} size={15} />
          <h2 className={styles.title} id="outreach-thread-title">
            {title}
          </h2>
        </header>
        {/* Says what this is before the thread does: a message, and one that came IN. The card carries
            the same badge, from the same component, so the two surfaces cannot describe the row
            differently. */}
        <div className={styles.kindRow}>
          <OutreachKindBadge />
        </div>

        {entries.length === 0 ? (
          <p className={styles.empty}>{t('tasks.outreach.emptyThread')}</p>
        ) : (
          <ol className={styles.thread}>
            {entries.map((m, i) => {
              const exact = m.at ? new Date(m.at).toLocaleString() : undefined;
              const showExact = revealed === i;
              return (
                <li key={i} className={m.mine ? `${styles.message} ${styles.mine}` : styles.message}>
                  <p className={styles.text} dir="auto">
                    {m.text}
                  </p>

                  {/* Relative by default, exact on hover (title) or tap (button). A message with no
                      recorded date shows nothing rather than a guess. */}
                  {m.at ? (
                    <button
                      type="button"
                      className={styles.when}
                      title={exact}
                      aria-label={exact}
                      onClick={() => setRevealed(showExact ? null : i)}
                    >
                      {showExact ? exact : m.relative}
                    </button>
                  ) : null}

                  {/* Where the thread handed the turn to the user, and what became of it. Derived from
                      the trail — the moment the agent asked for an answer lives in the user's own
                      conversation, never on the outreach, so who spoke next is the only honest source.
                      A reply sent from this dialog resolves the last ask, so an entry still marked
                      "waiting on you" is re-read against what we have since sent. */}
                  {m.awaitedYou ? (
                    <span className={styles.turn}>
                      {m.resolvedByIndex === undefined ? (
                        <Badge variant="waiting-on-you">{t('tasks.outreach.awaitingYou')}</Badge>
                      ) : (
                        <span className={styles.resolved}>{t('tasks.outreach.youAnswered')}</span>
                      )}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )}

        {/* Replying is the first thing anyone opening a message wants to do, so it is here rather than a
            step away — and it is the REAL composer, so attachments and dictation work. Pressing send
            hands the message to the agent in a conversation; see OutreachReplyComposer for why that is
            the honest shape rather than a direct send. */}
        <OutreachReplyComposer outreachId={task.id} agentName={agent.short} onHandedOff={closeModal} />

        {/* The other honest answer to a message: nothing. Shown on any thread that is not already
            finished — not just the ones badged "waiting on you". The rows that bucket to In progress
            (an empty thread, or one whose last author cannot be resolved) are stuck in exactly the same
            way and are HARDER to clear, since they are not even claiming the reader; gating on
            ACTION_NEEDED would have left them with no way out at all. A settled row is excluded because
            there the button would be a no-op, which reads as one more thing to deal with. It sits UNDER
            the composer because replying is the primary act; this is the way out, not the offer. */}
        {task.status !== 'COMPLETED' ? (
          <OutreachNoReplyNeeded outreachId={task.id} onCompleted={closeModal} />
        ) : null}
      </div>
    </Modal>
  );
}
